import React from "react";
import { Alert, Divider, Spin, Tag, Typography } from "antd";
import { getIntervalLabel, getPrice } from "./buyLicenseUtils";
import { formatEUR } from "./buyLicenseUtils.js";
import { ILicenseProducts } from "../../types/types";
import { useLanguage } from "src/providers/LanguageProvider";

const { Text } = Typography;

type Props = {
  loading?: boolean;
  error?: string | null;
  items: ILicenseProducts[]; // children: ILicenseProducts[]
};

const getProductBadge = (item: any, t: (k: string) => string) => {
  if (item?.IsRequired) return { color: "blue", text: t("REQUIRED") };
  if (item?.IsPurchasable === false) return { color: "orange", text: t("NOT_PURCHASABLE") };
  return { color: "default", text: t("OPTIONAL") };
};

// Tek bir düğümü (ve altlarını) render eder
const TreeRow: React.FC<{ item: any; level?: number; t: (k: string) => string }> = ({ item, level = 0, t }) => {
  if (!item) return null;

  const badge = getProductBadge(item, t);
  const price = formatEUR(getPrice(item));
  const hasChildren = Array.isArray(item?.children) && item.children.length > 0;

  return (
    <>
      <div
        key={item.Id}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: "8px 10px",
          marginLeft: level * 16, // hiyerarşi girintisi
          background: level > 0 ? "#fafafa" : undefined,
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Tag color={badge.color}>{badge.text}</Tag>
          <div>
            <div style={{ fontWeight: 600 }}>{item.Name}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{getIntervalLabel(!!item.IsMonthly)}</div>
          </div>
        </div>

        <div style={{ fontWeight: 600 }}>
          {price}
          {item.IsMonthly ? ` ${t("PER_MONTH")}` : ""}
        </div>
      </div>

      {hasChildren &&
        item.children.map((child: any) => (
          <TreeRow key={child?.Id ?? `${item.Id}-child`} item={child} level={level + 1} t={t} />
        ))}
    </>
  );
};

const BuyLicenseNecessaryList: React.FC<Props> = ({ loading, error, items = [] }) => {
  const { t } = useLanguage();

  const shouldRender = !!loading || !!error || (items?.length ?? 0) > 0;
  if (!shouldRender) return null;

  const hasAny = (items?.length ?? 0) > 0;

  return (
    <>
      <Divider style={{ margin: "8px 0" }} />
      <Text strong>
        {t("AUTO_ADDED_SUBPRODUCTS")} {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
      </Text>

      {error && <Alert type="error" message={error} showIcon style={{ marginTop: 8 }} />}

      {!loading && !error && !hasAny && (
        <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
          {t("NO_REQUIRED_SUBPRODUCTS")}
        </Text>
      )}

      {!loading && !error && hasAny && (
        <>
          <Alert
            banner
            type="info"
            style={{ marginTop: 6, marginBottom: 8 }}
            message={t("AUTO_ADDED_INFO")}
          />
          <div style={{ display: "grid", gap: 8 }}>
            {items.map((item) => (
              <TreeRow key={item?.Id} item={item} t={t} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default BuyLicenseNecessaryList;
